import type { ApiDate, CreateFundRequestInput, CreateReferralInput, CustomerPayment } from '../services/request/CustomerPortal';
import type { Messages } from '../i18n/types';
import { currentIntlLocale, currentLanguage } from '../i18n/store';

export type PortalValidationKey = keyof Messages['portal']['validation'];

export function toDate(value: ApiDate): Date | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d, h = 0, min = 0] = value.map(Number);
    const date = new Date(y, m - 1, d, h, min);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  if (typeof value === 'string') {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

export function formatDate(value: ApiDate): string | undefined {
  const date = toDate(value);
  return date ? date.toLocaleDateString(currentIntlLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : undefined;
}

export function formatDateTime(value: ApiDate): string | undefined {
  const date = toDate(value);
  return date
    ? date.toLocaleString(currentIntlLocale(), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : undefined;
}

export function monthKey(value: ApiDate): string | undefined {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})/.exec(value);
    if (match) return `${match[1]}-${match[2]}`;
  }
  if (Array.isArray(value) && value.length >= 2) {
    const year = Number(value[0]);
    const month = Number(value[1]);
    if (year && month >= 1 && month <= 12) return `${year}-${String(month).padStart(2, '0')}`;
  }
  return undefined;
}

/** Whole rupees, as stored by the service. Customer screens are never masked. */
export function inr(amount: number | undefined | null): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
}

/** English uses ordinals (1st, 2nd); the Indian languages read the plain number naturally. */
export function ordinalDay(day: number | undefined): string | undefined {
  if (!day || day < 1 || day > 31) return undefined;
  if (currentLanguage() !== 'en') return String(day);
  const mod = day % 100;
  const suffix = mod >= 11 && mod <= 13 ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  return `${day}${suffix}`;
}

export function humanize(value: string | undefined, fallback = 'On file'): string {
  if (!value) return fallback;
  const cleaned = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function firstName(name: string | undefined): string | undefined {
  const trimmed = name?.trim();
  return trimmed ? trimmed.split(/\s+/)[0] : undefined;
}

export interface MonthlyPaymentPoint {
  key: string;
  label: string;
  total: number;
}

/** Groups recorded payment amounts by the month the service stored. Gap months are zero. */
export function monthlyPaymentTotals(payments: Pick<CustomerPayment, 'createdDate' | 'amount'>[]): MonthlyPaymentPoint[] {
  const totals = new Map<string, number>();
  payments.forEach(payment => {
    const key = monthKey(payment.createdDate);
    if (!key || typeof payment.amount !== 'number' || !Number.isFinite(payment.amount)) return;
    totals.set(key, (totals.get(key) ?? 0) + payment.amount);
  });
  const keys = Array.from(totals.keys()).sort();
  if (keys.length === 0) return [];
  const [startYear, startMonth] = keys[0].split('-').map(Number);
  const [endYear, endMonth] = keys[keys.length - 1].split('-').map(Number);
  const points: MonthlyPaymentPoint[] = [];
  const monthLabel = new Intl.DateTimeFormat(currentIntlLocale(), { month: 'short', year: 'numeric' });
  let year = startYear;
  let month = startMonth;
  while (year < endYear || (year === endYear && month <= endMonth)) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    points.push({ key, label: monthLabel.format(new Date(year, month - 1, 1)), total: totals.get(key) ?? 0 });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return points;
}

export function wholeRupees(value: string): number | undefined {
  const digits = value.replace(/[^\d]/g, '');
  if (!digits) return undefined;
  const amount = Number(digits);
  return Number.isSafeInteger(amount) && amount >= 1 ? amount : undefined;
}

export function monthlyRate(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0.1 && parsed <= 15 ? parsed : undefined;
}

export function indianMobile(value: string): string | undefined {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) ? digits : undefined;
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function referralError(input: CreateReferralInput): PortalValidationKey | undefined {
  if (input.prospectName.trim().length < 2) return 'referralName';
  if (!isEmail(input.prospectEmail)) return 'email';
  if (!indianMobile(input.prospectPhone)) return 'mobile';
  if (!input.amountRequired || input.amountRequired < 1) return 'referralAmount';
  return undefined;
}

export function fundRequestError(input: CreateFundRequestInput): PortalValidationKey | undefined {
  if (!input.requestedAmount || input.requestedAmount < 1) return 'fundExact';
  if (!input.minimumAmount || input.minimumAmount < 1) return 'fundMinimum';
  if (input.minimumAmount > input.requestedAmount) return 'fundMinimumHigher';
  if (!input.monthlyInterestRate || input.monthlyInterestRate < 0.1 || input.monthlyInterestRate > 15) {
    return 'fundRate';
  }
  if (input.purpose.trim().length < 8) return 'fundPurpose';
  return undefined;
}
